// MegaSquirt Diagnostic — free upload + scan, paid per-fix or $100 unlimited bundle.
// Standalone route at /megasquirt-diagnose.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, CheckCircle2, AlertTriangle, Loader2, Download,
  ShieldCheck, Info, Zap, FileText, ArrowRight, XCircle, Wrench,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_STYLES = {
  critical: { dot: 'bg-red-500', text: 'text-red-400',    border: 'border-red-500/40',    bg: 'bg-red-500/10',    label: 'CRITICAL' },
  high:     { dot: 'bg-orange-500', text: 'text-orange-400', border: 'border-orange-500/40', bg: 'bg-orange-500/10', label: 'HIGH' },
  medium:   { dot: 'bg-amber-400',  text: 'text-amber-300',  border: 'border-amber-400/40',  bg: 'bg-amber-400/10',  label: 'MEDIUM' },
  low:      { dot: 'bg-zinc-400',   text: 'text-zinc-300',   border: 'border-zinc-500/40',   bg: 'bg-zinc-500/10',   label: 'LOW' },
};

// --------------------------- UPLOAD ---------------------------
function UploadPanel({ onScanComplete }) {
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const dropRef = useRef(null);

  const runScan = async () => {
    if (!file) return;
    setScanning(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/megasquirt/scan`, { method: 'POST', body: fd });
      if (!res.ok) {
        const detail = await res.text();
        throw new Error(`Scan failed (${res.status}): ${detail.slice(0, 200)}`);
      }
      const data = await res.json();
      onScanComplete(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setScanning(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto">
      <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-3">
        // Step 1 — Upload your .msq
      </div>
      <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-black text-white mb-4">
        Diagnose your MegaSquirt build
      </h2>
      <p className="text-sm text-zinc-400 mb-8 leading-relaxed">
        Upload your TunerStudio <code className="text-[#FF5E1A]">.msq</code> project file.
        We run 20 sanity checks against MS3/MS3X firmware and flag every real issue
        we find. <span className="text-emerald-400 font-bold">Scan is free.</span>{' '}
        Only pay for the fixes you want applied.
      </p>

      <div
        ref={dropRef}
        onDrop={onDrop}
        onDragOver={(e) => e.preventDefault()}
        className="border-2 border-dashed border-[#27272A] hover:border-[#FF5E1A]/60 rounded-sm p-10 text-center transition-colors bg-[#0A0A0A]"
        data-testid="msq-upload-zone"
      >
        <Upload className="w-10 h-10 mx-auto mb-4 text-[#FF5E1A]" />
        <p className="text-white font-bold text-sm mb-2">
          {file ? file.name : 'Drop your .msq file here'}
        </p>
        <p className="text-xs text-zinc-500 mb-4">or click below to browse — max 5 MB</p>
        <input
          id="msq-file-input"
          type="file"
          accept=".msq,.xml"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="hidden"
          data-testid="msq-file-input"
        />
        <label
          htmlFor="msq-file-input"
          className="inline-block cursor-pointer px-5 py-2 border border-[#27272A] hover:border-[#FF5E1A] text-xs font-bold tracking-[0.12em] uppercase text-white rounded-sm transition-colors"
        >
          Browse files
        </label>
      </div>

      {error && (
        <div className="mt-4 p-3 border border-red-500/40 bg-red-500/10 rounded-sm text-xs text-red-300" data-testid="msq-scan-error">
          {error}
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <div className="text-[10px] font-['JetBrains_Mono'] text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
          Your .msq stays private — never shared, never used for anything else
        </div>
        <button
          onClick={runScan}
          disabled={!file || scanning}
          data-testid="msq-run-scan-btn"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF5E1A] hover:bg-[#FF7A42] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold tracking-[0.14em] uppercase rounded-sm transition-all"
        >
          {scanning ? <><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</> : <>Run free scan <ArrowRight className="w-4 h-4" /></>}
        </button>
      </div>
    </motion.div>
  );
}

// --------------------------- RESULTS ---------------------------
function IssueCard({ issue, selected, onToggle, disabled }) {
  const style = SEVERITY_STYLES[issue.severity] || SEVERITY_STYLES.low;
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={`border ${selected ? 'border-[#FF5E1A] bg-[#FF5E1A]/5' : style.border + ' ' + style.bg} rounded-sm transition-all`}
      data-testid={`msq-issue-${issue.id}`}
    >
      <div className="flex items-start gap-3 p-4">
        <button
          type="button"
          onClick={() => !disabled && onToggle(issue.id)}
          disabled={disabled}
          data-testid={`msq-issue-toggle-${issue.id}`}
          className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-sm border flex items-center justify-center transition-colors ${
            selected ? 'bg-[#FF5E1A] border-[#FF5E1A]' : 'bg-transparent border-[#3a3a3d] hover:border-[#FF5E1A]/60'
          } ${disabled ? 'opacity-40' : ''}`}
          aria-label={`Select ${issue.title}`}
        >
          {selected && <CheckCircle2 className="w-3.5 h-3.5 text-[#0A0A0A]" />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[9px] font-['JetBrains_Mono'] px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${style.text} ${style.bg} border ${style.border}`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${style.dot}`}></span>
              {style.label}
            </span>
            <span className={`text-[9px] font-['JetBrains_Mono'] px-1.5 py-0.5 rounded-sm uppercase tracking-wider border ${
              issue.category === 'table'
                ? 'text-[#FF5E1A] border-[#FF5E1A]/40 bg-[#FF5E1A]/10'
                : 'text-cyan-400 border-cyan-500/40 bg-cyan-500/10'
            }`}>
              {issue.category} · +${issue.price}
            </span>
          </div>
          <h4 className="text-sm font-bold text-white mb-1">{issue.title}</h4>
          <p className="text-xs text-zinc-400 leading-relaxed">{issue.headline}</p>

          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`msq-issue-expand-${issue.id}`}
            className="mt-2 text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-[#FF5E1A] hover:text-[#FF7A42] transition-colors flex items-center gap-1"
          >
            <Info className="w-3 h-3" />
            {expanded ? 'Hide details' : 'See details & fix instructions'}
          </button>

          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="mt-3 p-3 border border-[#27272A] bg-[#0A0A0A] rounded-sm space-y-2">
                  <div>
                    <div className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.16em] text-zinc-500 mb-1">Why this matters</div>
                    <p className="text-xs text-zinc-300 leading-relaxed">{issue.detail}</p>
                  </div>
                  <div>
                    <div className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.16em] text-emerald-400 mb-1 flex items-center gap-1">
                      <Wrench className="w-3 h-3" />
                      Manual fix steps (free — do it yourself in TunerStudio)
                    </div>
                    <p className="text-xs text-zinc-300 leading-relaxed">{issue.fix_instructions}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function ResultsPanel({ scan, onReset, onRedirected }) {
  const [selected, setSelected] = useState(new Set());
  const [bundle, setBundle] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const issues = scan.issues || [];
  const toggle = (id) => {
    if (bundle) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedIssues = issues.filter((i) => selected.has(i.id));
  const perFixTotal = selectedIssues.reduce((a, i) => a + (i.price || 0), 0);
  const displayTotal = bundle ? 100 : perFixTotal;

  const startCheckout = async () => {
    if (!bundle && selected.size === 0) return;
    setCheckoutLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/megasquirt/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scan_id: scan.scan_id,
          fix_ids: bundle ? [] : Array.from(selected),
          bundle,
          origin_url: `${window.location.origin}/megasquirt-diagnose`,
        }),
      });
      if (!res.ok) {
        const detail = await res.text();
        alert(`Could not start checkout (${res.status}): ${detail.slice(0, 200)}`);
        setCheckoutLoading(false);
        return;
      }
      const data = await res.json();
      if (data.url) {
        onRedirected?.();
        window.location.href = data.url;
      } else {
        alert('No checkout URL returned.');
        setCheckoutLoading(false);
      }
    } catch (err) {
      alert(`Network error: ${err.message}`);
      setCheckoutLoading(false);
    }
  };

  const report = scan.report || {};
  const verdictStyle =
    report.critical ? 'border-red-500/60 bg-red-500/10 text-red-300'
    : report.high >= 3 ? 'border-orange-500/60 bg-orange-500/10 text-orange-300'
    : report.high ? 'border-amber-400/60 bg-amber-400/10 text-amber-200'
    : 'border-emerald-500/60 bg-emerald-500/10 text-emerald-300';

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-1">
            // Step 2 — Review findings
          </div>
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-black text-white">
            Scan complete: <span className="text-[#FF5E1A]">{scan.filename}</span>
          </h2>
          {scan.firmware && (
            <p className="text-xs text-zinc-500 font-['JetBrains_Mono'] mt-1">
              Firmware: {scan.firmware}
              {scan.signature ? ` · ${scan.signature}` : ''}
            </p>
          )}
        </div>
        <button
          onClick={onReset}
          data-testid="msq-scan-another-btn"
          className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-400 hover:text-white transition-colors"
        >
          Scan another file
        </button>
      </div>

      {/* Verdict banner */}
      <div className={`border rounded-sm p-4 ${verdictStyle}`} data-testid="msq-verdict-banner">
        <div className="flex items-center gap-2 mb-2">
          {report.critical ? <AlertTriangle className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
          <span className="font-bold text-sm uppercase tracking-wider font-['JetBrains_Mono']">
            {report.issue_count || 0} issue{(report.issue_count || 0) === 1 ? '' : 's'} found
          </span>
        </div>
        <p className="text-sm">{report.verdict}</p>
        <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-['JetBrains_Mono']">
          {['critical', 'high', 'medium', 'low'].map((sev) => (
            <span key={sev} className={`px-2 py-0.5 border rounded-sm ${SEVERITY_STYLES[sev].border} ${SEVERITY_STYLES[sev].bg} ${SEVERITY_STYLES[sev].text}`}>
              {report[sev] || 0} {sev}
            </span>
          ))}
        </div>
      </div>

      {/* Issues list */}
      <div className="space-y-3">
        {issues.length === 0 ? (
          <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-6 text-center">
            <ShieldCheck className="w-10 h-10 mx-auto text-emerald-400 mb-3" />
            <p className="text-white font-bold">Clean build — nothing flagged.</p>
            <p className="text-xs text-zinc-400 mt-1">
              All 20 sanity checks passed. You're clear to run it.
            </p>
          </div>
        ) : (
          issues.map((i) => (
            <IssueCard
              key={i.id}
              issue={i}
              selected={bundle || selected.has(i.id)}
              onToggle={toggle}
              disabled={bundle}
            />
          ))
        )}
      </div>

      {issues.length > 0 && (
        <div className="sticky bottom-4 bg-[#0F0F0F] border border-[#FF5E1A]/30 rounded-sm p-5 shadow-[0_0_40px_rgba(255,94,26,0.2)]">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bundle}
                  onChange={(e) => setBundle(e.target.checked)}
                  data-testid="msq-bundle-toggle"
                  className="mt-1 accent-[#FF5E1A]"
                />
                <div>
                  <div className="text-sm font-bold text-white">
                    Unlimited Fix Bundle — flat <span className="text-[#FF5E1A]">$100</span>
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">
                    Patch every detected issue, regardless of count.{' '}
                    {report.estimated_per_fix_total > 100 && (
                      <span className="text-emerald-400">
                        Save ${(report.estimated_per_fix_total - 100).toFixed(0)} vs per-fix.
                      </span>
                    )}
                  </div>
                </div>
              </label>
            </div>

            <div className="text-right">
              <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500">
                {bundle ? 'Bundle total' : `${selected.size} fix${selected.size === 1 ? '' : 'es'} selected`}
              </div>
              <div className="text-2xl font-black text-white font-['JetBrains_Mono']" data-testid="msq-grand-total">
                ${displayTotal.toFixed(2)}
              </div>
            </div>

            <button
              onClick={startCheckout}
              disabled={checkoutLoading || (!bundle && selected.size === 0)}
              data-testid="msq-checkout-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF5E1A] hover:bg-[#FF7A42] disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold tracking-[0.14em] uppercase rounded-sm transition-all"
            >
              {checkoutLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Apply fixes → checkout <ArrowRight className="w-4 h-4" /></>}
            </button>
          </div>
          <div className="mt-3 text-[10px] font-['JetBrains_Mono'] text-zinc-500 flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3 text-emerald-400" />
            Fix instructions are always free. Paying unlocks a patched .msq file download — no risk of typos.
          </div>
        </div>
      )}
    </motion.div>
  );
}

// --------------------------- POST-PAYMENT ---------------------------
function PostPaymentPanel({ scanId, onReset }) {
  const [status, setStatus] = useState({ paid: false, patched_available: false });
  const [polls, setPolls] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/megasquirt/status/${scanId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) {
          setStatus(data);
          if (!data.paid && polls < 8) {
            setTimeout(() => setPolls((p) => p + 1), 1500);
          }
        }
      } catch {
        /* keep polling */
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [scanId, polls]);

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto text-center">
      {status.paid ? (
        <>
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-7 h-7 text-emerald-400" />
          </div>
          <h2 className="font-['Outfit'] text-3xl font-black text-white mb-2">Payment received</h2>
          <p className="text-sm text-zinc-400 mb-6">
            {status.paid_fix_ids?.length} fix{status.paid_fix_ids?.length === 1 ? '' : 'es'} applied.
            Your patched .msq is ready.
          </p>
          <a
            href={`${API_URL}/api/megasquirt/download/${scanId}`}
            data-testid="msq-download-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-xs font-bold tracking-[0.14em] uppercase rounded-sm transition-all"
          >
            <Download className="w-4 h-4" /> Download patched .msq
          </a>
          <p className="mt-6 text-[11px] text-zinc-500 font-['JetBrains_Mono'] leading-relaxed">
            Back up your current project first. Load the patched file into TunerStudio,
            verify all sensor readings before cranking.
          </p>
          <button
            onClick={onReset}
            data-testid="msq-scan-another-after-pay"
            className="mt-6 text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500 hover:text-white transition-colors"
          >
            Scan another file
          </button>
        </>
      ) : (
        <>
          <Loader2 className="w-8 h-8 mx-auto text-[#FF5E1A] animate-spin mb-4" />
          <h2 className="font-['Outfit'] text-2xl font-black text-white mb-2">Verifying payment…</h2>
          <p className="text-sm text-zinc-400">Stripe usually confirms in under 10 seconds.</p>
        </>
      )}
    </motion.div>
  );
}

// --------------------------- ROOT ---------------------------
export default function MegaSquirtDiagnose() {
  const [scan, setScan] = useState(null);
  const [paidScanId, setPaidScanId] = useState(null);
  const [cancelled, setCancelled] = useState(false);

  // Read Stripe redirect-back params on mount
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const payment = p.get('payment');
    const scanId = p.get('scan_id');
    if (payment === 'success' && scanId) {
      setPaidScanId(scanId);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (payment === 'cancelled') {
      setCancelled(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Set page title
  useEffect(() => {
    const prev = document.title;
    document.title = 'MegaSquirt Diagnostic — Free Scan, Pay Per Fix | AI Tuner Pro';
    return () => { document.title = prev; };
  }, []);

  const reset = useCallback(() => {
    setScan(null);
    setPaidScanId(null);
    setCancelled(false);
  }, []);

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white" data-testid="megasquirt-diagnose-page">
      {/* Hero strip */}
      <section className="border-b border-[#27272A] py-12 sm:py-16">
        <div className="container mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 rounded-sm">
            <Zap className="w-3 h-3 text-[#FF5E1A]" />
            <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-[#FF5E1A]">
              MegaSquirt Build Troubleshooter
            </span>
          </div>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-4">
            Your build won't run?<br />
            <span className="bg-gradient-to-r from-[#FF5E1A] to-[#FF7A42] bg-clip-text text-transparent">
              We find out why. Free.
            </span>
          </h1>
          <p className="text-sm sm:text-base text-zinc-400 max-w-xl mx-auto">
            Upload your <code className="text-[#FF5E1A]">.msq</code>, get a 20-rule diagnostic in under 10 seconds.
            Pay <span className="text-white font-bold">$15–$35 per fix</span> or <span className="text-white font-bold">$100 flat for unlimited</span>.
          </p>
        </div>
      </section>

      <section className="container mx-auto px-6 py-12 sm:py-16">
        {cancelled && (
          <div className="max-w-2xl mx-auto mb-8 p-3 border border-amber-500/40 bg-amber-500/10 rounded-sm text-xs text-amber-200 font-['JetBrains_Mono']">
            Checkout cancelled — no charge was made. Select your fixes and try again whenever you're ready.
          </div>
        )}

        {paidScanId ? (
          <PostPaymentPanel scanId={paidScanId} onReset={reset} />
        ) : scan ? (
          <ResultsPanel scan={scan} onReset={reset} onRedirected={() => {}} />
        ) : (
          <UploadPanel onScanComplete={setScan} />
        )}
      </section>

      {/* How it works strip */}
      <section className="border-t border-[#27272A] py-12">
        <div className="container mx-auto px-6">
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <Upload className="w-6 h-6 mx-auto text-[#FF5E1A] mb-2" />
              <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500 mb-1">01 / Upload</div>
              <p className="text-sm text-zinc-300">Drop your <code className="text-[#FF5E1A]">.msq</code>. 5 MB limit.</p>
            </div>
            <div className="text-center">
              <FileText className="w-6 h-6 mx-auto text-[#FF5E1A] mb-2" />
              <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500 mb-1">02 / Free scan</div>
              <p className="text-sm text-zinc-300">20 sanity rules. See every issue + fix steps.</p>
            </div>
            <div className="text-center">
              <Download className="w-6 h-6 mx-auto text-[#FF5E1A] mb-2" />
              <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-wider text-zinc-500 mb-1">03 / Patched file</div>
              <p className="text-sm text-zinc-300">Pay per fix (or $100 unlimited). Download patched .msq.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
